/**
 * The burgerhood class.
 * Handles functionality for the Supernav's responsive behaviors.
 * 
 * TODO: In the future, we may want to consider events for the nav changing from burger to full, etc.
 */

/* Dependencies --------------------------------------------- */

import { throttle } from 'throttle-debounce'
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll'

/* Main class ----------------------------------------------- */

function singleton( config ) {

    /** Setup --------------------------- */

    /**
     * The return object.
	 * 
     * Tracks stuff in the old-school way of doing class-like structures.
	 * Believe it or not, I didn't want to do half of the codebase like this and the other half a different way.
     */
    let object = { config: config }

	/** The state ----------------------- */

	object.state = {
		supernav            : null,
		navigation          : null,
		logo                : null,
		primaryMenuWrapper  : null,
		secondaryMenuWrapper: null,
		tertiaryMenuWrapper : null,
		toppingsMenuWrapper : null,
		toggle              : null,
		toggleButton        : null,
		loginModal          : null,
		searchModal         : null,
		isOpen              : false,
		lastScrollY         : 0,
		lastViewportWidth   : null
	}

    /** Events -------------------------- */

    /**
     * Wires up event listeners.
     */
    object.listen = function () {
		jQuery( window ).on( 'resize', throttle( object.config.resizeThrottle, object.monitor ) )
		jQuery( object.config.eventDelegatorSelector ).on( 'click', object.config.toggleButtonSelector , object.handleToggleButtonClick )
    }

	/** Handlers ------------------------ */

	/**
	 * Handles clicking of the toggle button.
	 * Toggles the state of the navigation.
	 */
	object.handleToggleButtonClick = function() {
		object.setOpenState( ! object.state.isOpen )
	}

	/** Main methods -------------------- */

	/**
	 * Converts the Supernav into its full form.
	 */
	object.becomeFull = function() {

		object.markDeterminate( false )

		object.state.supernav.removeClass( object.config.isHamburgerClass )
		object.state.supernav.addClass( object.config.isFullClass )

		// First, we have to detach all of the elements we have to move.

		object.state.primaryMenuWrapper.detach()
		object.state.secondaryMenuWrapper.detach()
		object.state.tertiaryMenuWrapper.detach()
		object.state.searchModal.detach()

		// Then, we have to put them all back in the correct order.

		object.state.navigation.append( object.state.tertiaryMenuWrapper )
		object.state.navigation.append( object.state.secondaryMenuWrapper )
		object.state.navigation.append( object.state.primaryMenuWrapper )
		object.state.searchModal.insertAfter( object.state.loginModal )

		// Finally, we close the nav just in case.

		object.setOpenState( false )
		object.markDeterminate( true )
	}

	/**
	 * Converts the Supernav into its burger form.
	 */
	object.becomeBurger = function() {

		object.markDeterminate( false )

		object.state.supernav.removeClass( object.config.isFullClass )
		object.state.supernav.addClass( object.config.isHamburgerClass )

		// Then, we have to detach all of the elements we have to move.

		object.state.primaryMenuWrapper.detach()
		object.state.secondaryMenuWrapper.detach()
		object.state.tertiaryMenuWrapper.detach()
		object.state.searchModal.detach()

		// Then, we have to put them all back in the correct order.

		object.state.navigation.append( object.state.searchModal )
		object.state.navigation.append( object.state.primaryMenuWrapper )
		object.state.navigation.append( object.state.tertiaryMenuWrapper )
		object.state.navigation.append( object.state.secondaryMenuWrapper )

		// Sometimes, this can cause weird race conditions, so we push it to the next tick.

		window.setTimeout( () => object.markDeterminate( true ), 0 )
	}	

	/** Secondary methods --------------- */

	/**
	 * Monitors the viewport size and triggers DOM manipulation if needed.
	 */
	object.monitor = function() {

		const viewportWidth = window.innerWidth

		const wasNull   = object.state.lastViewportWidth === null
		const wasFull   = object.state.lastViewportWidth >= object.config.hamburgerBreakpoint
		const wasBurger = object.state.lastViewportWidth < object.config.hamburgerBreakpoint

		const isFull   = viewportWidth >= object.config.hamburgerBreakpoint
		const isBurger = viewportWidth < object.config.hamburgerBreakpoint

		if ( isFull && ( wasBurger || wasNull ) ) {
			object.becomeFull()
		}
		else if ( isBurger && ( wasFull || wasNull )  ) {
			object.becomeBurger()
		}

		object.state.lastViewportWidth = viewportWidth		
	}

	/**
	 * Sets the open state of the navigation.
	 */
	object.setOpenState = function( toOpen ) {

		object.state.isOpen = toOpen

		if ( toOpen ) {
			disablePageScroll()
			object.state.supernav.removeClass( object.config.isClosedClass )
			object.state.supernav.addClass( object.config.isOpenClass )
		}
		else {
			enablePageScroll()
			object.state.supernav.removeClass( object.config.isOpenClass )
			object.state.supernav.addClass( object.config.isClosedClass )
		}

		object.state.toggleButton.attr( 'aria-expanded', toOpen )
		object.state.supernav.trigger( toOpen ? openEventName : closeEventName )
	}

	/**
	 * Marks the nav's state as determinate.
	 * May delay removing a class depending on whether animations need to sort out first.
	 */
	object.markDeterminate = function( isDeterminate ) {

		if ( isDeterminate ) {
			object.state.supernav.removeClass( object.config.isIndeterminateClass )
			object.state.supernav.addClass( object.config.isDeterminateClass )
		}
		else {
			object.state.supernav.removeClass( object.config.isDeterminateClass )
			object.state.supernav.addClass( object.config.isIndeterminateClass )
		}
	}

    /** Helpers ------------------------- */

    /** Initialization ------------------ */

	/**
	 * Initializes the burgerhood functionality.
	 * Basically just queries up elements we'll need to use often.
	 */
	object.initialize = function() {
		object.state.supernav             = jQuery( object.config.selector )
		object.state.navigation           = jQuery( object.config.navigationSelector )
		object.state.logo                 = jQuery( object.config.logoSelector )
		object.state.primaryMenuWrapper   = jQuery( object.config.primaryMenuWrapperSelector )
		object.state.secondaryMenuWrapper = jQuery( object.config.secondaryMenuWrapperSelector )
		object.state.tertiaryMenuWrapper  = jQuery( object.config.tertiaryMenuWrapperSelector )
		object.state.toppingsMenuWrapper  = jQuery( object.config.toppingsMenuWrapperSelector )
		object.state.toggle               = jQuery( object.config.toggleSelector )
		object.state.toggleButton         = jQuery( object.config.toggleSelector )
		object.state.loginModal           = jQuery( object.config.loginModalSelector )
		object.state.searchModal          = jQuery( object.config.searchModalSelector )

		object.monitor()
		object.listen()
	}

	object.initialize()
    return object
}

/** Export module ------------------------------------------- */

export const openEventName  = 'cuSupernavBurgerhoodOpen'
export const closeEventName = 'cuSupernavBurgerhoodClose'

export default {

    init( config = {} ) {

		if ( ! config.namespace ) return

		config.selector = `.js-${config.namespace}`
        let elements = document.querySelectorAll( config.selector )

        if ( ! elements.length || ! jQuery ) return

        config = Object.assign(
            {
				
                // General config

				hamburgerBreakpoint: 1024,
				resizeThrottle     : 100,

				// Selectors

				eventDelegatorSelector        : 'body',
				navigationSelector            : `.js-${config.namespace}__navigation`,
				logoSelector                  : `.js-${config.namespace}__logo`,
				primaryMenuWrapperSelector    : `.js-${config.namespace}__menu-wrapper.is-${config.namespace}-menu-location-primary`,
				secondaryMenuWrapperSelector  : `.js-${config.namespace}__menu-wrapper.is-${config.namespace}-menu-location-secondary`,
				tertiaryMenuWrapperSelector   : `.js-${config.namespace}__menu-wrapper.is-${config.namespace}-menu-location-tertiary`,
				toppingsMenuWrapperSelector   : `.js-${config.namespace}__menu-wrapper.is-${config.namespace}-menu-location-toppings`,
				toggleSelector                : `.js-${config.namespace}__toggle`,
				toggleButtonSelector          : `.js-${config.namespace}__toggle-button`,
				loginModalSelector            : `.js-${config.namespace}__modal#${config.namespace}-modal-login`,
				searchModalSelector           : `.js-${config.namespace}__modal#${config.namespace}-modal-search`,				

				// State classes

				isFullClass         : `is-${config.namespace}-full`,
				isHamburgerClass    : `is-${config.namespace}-hamburger`,
				isIndeterminateClass: `is-${config.namespace}-indeterminate`,
				isDeterminateClass  : `is-${config.namespace}-determinate`,
				isOpenClass         : `is-${config.namespace}-open`,
				isClosedClass       : `is-${config.namespace}-closed`
            },
            config
        )

        return singleton( config )
    }
}
